import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { opsApi, apiError, fmtNum, GRAD, FONT_HEAD, STAGES, STAGE_META, Pagination, ROLE_RANK } from './opsApi';
import { institutionRowStatus } from './institutionStatus';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Building2, Plus, Search, MapPin, GraduationCap, Users, Cpu } from 'lucide-react';

export default function OpsInstitutions({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [stageFilter, setStageFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const isManager = (ROLE_RANK[user.role] || 0) >= 2;

  const [form, setForm] = useState({
    name: '', institution_type: 'school', contact_name: '', contact_email: '', contact_phone: '',
    city: '', assigned_rep_id: '', expected_teachers: 0, expected_students: 0,
    billing_cycle: 'yearly', notes: '',
  });

  const load = useCallback(() => {
    opsApi.get('/institutions', { params: { page, page_size: 25, stage: stageFilter || undefined, search: search || undefined } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [page, stageFilter, search]);

  useEffect(() => { const t = setTimeout(load, search ? 350 : 0); return () => clearTimeout(t); }, [load, search]);

  useEffect(() => {
    if (isManager) opsApi.get('/team', { params: { page_size: 100 } }).then((r) => setTeam(r.data.items.filter((m) => m.role === 'sales_rep' && m.status === 'active'))).catch(() => {});
  }, [isManager]);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Institution name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, expected_teachers: Number(form.expected_teachers) || 0, expected_students: Number(form.expected_students) || 0 };
      if (!payload.assigned_rep_id) delete payload.assigned_rep_id;
      const { data: inst } = await opsApi.post('/institutions', payload);
      toast.success('Institution lead created');
      setShowCreate(false);
      navigate(`/sales/institutions/${inst.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div className="space-y-6" data-testid="ops-institutions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>Institutions</h1>
          <p className="text-sm text-slate-500 mt-1">Lifecycle, licensing & penetration for every institution in your scope</p>
        </div>
        <Button data-testid="add-institution-btn" onClick={() => setShowCreate(true)} className="text-white rounded-xl h-10 border-0" style={{ background: GRAD }}>
          <Plus className="w-4 h-4 mr-1.5" /> New institution lead
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="institution-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search institutions or cities…" className="pl-9 w-64 h-9 bg-white" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['', ...STAGES].map((s) => (
            <button key={s || 'all'} data-testid={`stage-filter-${s || 'all'}`} onClick={() => { setStageFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                stageFilter === s ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
              style={stageFilter === s ? { background: GRAD } : undefined}>
              {s ? STAGE_META[s].label : 'All'}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {!data ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#34A853]" /></div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No institutions found in your scope.</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
                  <th className="px-5 py-3 font-medium">Institution</th>
                  <th className="px-4 py-3 font-medium">Stage</th>
                  <th className="px-4 py-3 font-medium">Rep</th>
                  <th className="px-4 py-3 font-medium">Licence</th>
                  <th className="px-4 py-3 font-medium text-right">Teachers</th>
                  <th className="px-4 py-3 font-medium text-right">Students</th>
                  <th className="px-4 py-3 font-medium text-right">Penetration</th>
                  <th className="px-4 py-3 font-medium text-right">Kits</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <tr key={s.id} data-testid={`institution-row-${s.id}`} onClick={() => navigate(`/sales/institutions/${s.id}`)}
                    className="border-b border-slate-50 hover:bg-blue-50/30 cursor-pointer transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-slate-800">{s.name}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-2">
                        {s.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.city}</span>}
                        <span className="font-mono text-[10px] text-[#3B82F6]">{s.scope_path}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      {institutionRowStatus(s).kind !== 'stage' ? (
                        // School deleted, or suspended from Desk Ops — either way
                        // the sales stage ("Active") would be misleading here.
                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium border bg-red-50 text-red-700 border-red-200"
                              title={institutionRowStatus(s).title}
                              data-testid={`inst-school-${institutionRowStatus(s).kind}-${s.id}`}>
                          {institutionRowStatus(s).label}
                        </span>
                      ) : (
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${STAGE_META[s.stage]?.color}`}>
                          {STAGE_META[s.stage]?.label || s.stage}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-slate-600 text-xs">{s.assigned_rep_name || '—'}</td>
                    <td className="px-4 py-3.5 text-xs" data-testid={`institution-licence-${s.id}`}>
                      {s.licence_key ? (
                        <div className="leading-tight">
                          <div className="font-mono text-[11px] text-slate-700 truncate max-w-[140px]" title={s.licence_key}>{s.licence_key}</div>
                          <div className={`text-[10px] uppercase tracking-wider font-medium ${s.licence_status === 'active' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {s.licence_status || 'inactive'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-300 text-[11px] italic">Not activated</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums">
                      <span className="inline-flex items-center gap-1 text-slate-700"><GraduationCap className="w-3.5 h-3.5 text-slate-300" />
                        {s.usage ? fmtNum(s.usage.teachers_active) : <span className="text-slate-300">est. {s.expected_teachers}</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums">
                      <span className="inline-flex items-center gap-1 text-slate-700"><Users className="w-3.5 h-3.5 text-slate-300" />
                        {s.usage ? fmtNum(s.usage.students_licensed) : <span className="text-slate-300">est. {s.expected_students}</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums">
                      {s.penetration_pct != null ? <span className={`font-medium ${s.penetration_pct >= 50 ? 'text-green-600' : 'text-amber-600'}`}>{s.penetration_pct}%</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums">
                      <span className="inline-flex items-center gap-1 text-slate-600"><Cpu className="w-3.5 h-3.5 text-slate-300" />{s.kit_count ?? 0}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination data={data} onPage={setPage} />
          </>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>New institution lead</DialogTitle>
            <DialogDescription>Tagged to your scope ({user.scope_path}). Activate later to create its Desk tenant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <div>
              <Label className="text-xs">Institution name *</Label>
              <Input data-testid="create-institution-name" value={form.name} onChange={set('name')} placeholder="e.g. Sunrise International School" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Contact person</Label>
                <Input value={form.contact_name} onChange={set('contact_name')} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input data-testid="create-institution-city" value={form.city} onChange={set('city')} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Contact email</Label>
                <Input type="email" value={form.contact_email} onChange={set('contact_email')} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Contact phone</Label>
                <Input value={form.contact_phone} onChange={set('contact_phone')} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Expected teachers</Label>
                <Input type="number" min="0" value={form.expected_teachers} onChange={set('expected_teachers')} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Expected students</Label>
                <Input data-testid="create-institution-students" type="number" min="0" value={form.expected_students} onChange={set('expected_students')} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.institution_type} onValueChange={(v) => setForm((f) => ({ ...f, institution_type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="college">College</SelectItem>
                    <SelectItem value="coaching">Coaching Institute</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Billing cycle</Label>
                <div className="h-10 rounded-lg border border-gray-200 bg-gray-50 text-sm px-3 flex items-center text-gray-600 font-medium mt-1">
                  Yearly
                </div>
              </div>
            </div>
            {isManager && team.length > 0 && (
              <div>
                <Label className="text-xs">Assign sales rep</Label>
                <Select value={form.assigned_rep_id} onValueChange={(v) => setForm((f) => ({ ...f, assigned_rep_id: v }))}>
                  <SelectTrigger className="mt-1" data-testid="create-institution-rep"><SelectValue placeholder="Assign to yourself" /></SelectTrigger>
                  <SelectContent>{team.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name} ({m.employee_code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={set('notes')} rows={2} className="mt-1" placeholder="Context, decision makers, requirements…" />
            </div>
            <Button data-testid="create-institution-submit" onClick={create} disabled={saving}
              className="w-full text-white rounded-xl h-10 border-0" style={{ background: GRAD }}>
              {saving ? 'Creating…' : 'Create institution lead'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
